import { useState } from "react";
import { Package, Truck } from "lucide-react";
import { api } from "@/lib/api";

const statusLabel = (res) => {
  if (res.status === "pending_payment") return { text: "PAYMENT PENDING", color: "text-red" };
  if (res.status === "expired") return { text: "CHECKOUT EXPIRED — ORDER NOT PLACED", color: "text-red" };
  if (res.fulfillment_status === "fulfilled" || res.shipments?.length) return { text: "SHIPPED", color: "text-navy" };
  if (res.fulfillment_status === "inprocess") return { text: "BEING MADE", color: "text-navy" };
  return { text: "CONFIRMED — IN THE WORKS", color: "text-navy" };
};

function ResultCard({ res }) {
  const label = statusLabel(res);
  return (
    <div className="mt-10 bg-white shadow-print" data-testid="order-lookup-result">
      <div className="px-5 py-4 border-b border-ink/10 flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-college text-2xl text-navy" data-testid="order-lookup-number">{res.order_number}</p>
        <p className={`font-college text-lg ${label.color}`} data-testid="order-lookup-status">
          {label.text}
        </p>
      </div>
      <div className="px-5 py-4 space-y-2">
        {res.items.map((i) => (
          <p key={`${i.name}-${i.size}`} className="text-sm text-ink/80 flex items-center gap-2">
            <Package size={14} className="text-red shrink-0" /> {i.name} — {i.size} × {i.quantity}
          </p>
        ))}
        <p className="text-sm text-ink/60 pt-2">Total ${res.total} · shipping to {res.city}</p>
      </div>
      {res.shipments?.length > 0 && (
        <div className="px-5 py-4 border-t border-ink/10 space-y-2" data-testid="order-lookup-tracking">
          {res.shipments.map((sh) => (
            <p key={sh.tracking_number || sh.carrier} className="text-sm flex items-center gap-2">
              <Truck size={14} className="text-navy shrink-0" />
              {sh.carrier} — {sh.tracking_url ? (
                <a href={sh.tracking_url} target="_blank" rel="noreferrer" className="underline text-navy hover:text-red">
                  {sh.tracking_number}
                </a>
              ) : sh.tracking_number}
            </p>
          ))}
        </div>
      )}
      {!res.shipments?.length && res.status === "confirmed" && (
        <p className="px-5 py-4 border-t border-ink/10 text-sm text-ink/60">
          Tracking appears here the moment it ships.
        </p>
      )}
    </div>
  );
}

export default function OrderLookup() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setRes(null);
    try {
      setRes(await api.track(q.trim()));
    } catch (error) {
      setErr(error.response?.data?.detail || "No order found with that number");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 pt-14 pb-10" data-testid="order-lookup-page">
      <p className="font-college text-lg text-red">WHERE'S MY STUFF</p>
      <h1 className="font-college text-4xl sm:text-5xl text-navy mt-2" data-testid="order-lookup-heading">ORDER LOOKUP</h1>
      <p className="text-ink/70 mt-3 text-base">
        Paste the order number from your confirmation (looks like RUNRS-A1B2C3).
      </p>
      <p className="text-sm text-ink/60 mt-2" data-testid="order-lookup-support">
        Contact:{" "}
        <a href="mailto:runrs2026@gmail.com" data-testid="order-lookup-support-email" className="underline text-navy hover:text-red transition-colors">
          runrs2026@gmail.com
        </a>
      </p>

      <form onSubmit={lookup} className="flex gap-3 mt-8" data-testid="order-lookup-form">
        <input
          data-testid="order-lookup-input"
          required
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="RUNRS-XXXXXX"
          className="flex-1 min-w-0 bg-white px-4 py-3 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red uppercase"
        />
        <button
          data-testid="order-lookup-submit"
          type="submit"
          disabled={loading}
          className="bg-navy text-paper font-college text-xl px-6 py-3 shadow-print hover:bg-red transition-colors disabled:opacity-60"
        >
          {loading ? "LOOKING…" : "FIND IT"}
        </button>
      </form>

      {err && (
        <p className="mt-6 text-sm text-red" data-testid="order-lookup-error">{err}</p>
      )}

      {res && <ResultCard res={res} />}
    </div>
  );
}
